import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, DoorOpen, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Header() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const nav = [
    { to: "/pula", label: t("nav.pula", "Pula") },
    { to: "/", label: t("nav.home") },
    { to: "/how-it-works", label: t("nav.how_it_works") },
    { to: "/projects", label: t("nav.projects", "Projects") },
    { to: "/safety", label: t("nav.safety", "Safety") },
    { to: "/about", label: t("nav.about", "About") },
    { to: "/agent-portal", label: t("nav.agent_portal", "Agent Portal") },
    { to: "/legal-status", label: t("nav.legal", "Legal") },
    { to: "https://fair-connect-2.emergent.host", label: "Pula Community", external: true },
  ];

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-black/5"
      style={{ background: "rgba(253,252,248,0.85)", backdropFilter: "blur(18px)" }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-[68px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="brand-logo">
          <div className="w-9 h-9 rounded-full mv-green-bg flex items-center justify-center">
            <DoorOpen size={18} strokeWidth={1.75} className="text-white" />
          </div>
          <div className="leading-none">
            <div className="font-display text-[22px] mv-green-text tracking-tight">
              Mavula
              <span className="ml-1 font-accent italic text-[16px]" style={{ color: "var(--mv-gold)" }}>Investment</span>
            </div>
            <div className="text-[10px] tracking-[0.22em] uppercase" style={{ color: "var(--mv-muted)" }}>
              Open Doors
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8 ml-8">
          {nav.map((n, i) => {
            const testId = `nav-${["pula","home","how-it-works","projects","safety","about","agent-portal","legal-status","pula-community"][i]}`;
            if (n.external) {
              return (
                <a
                  key={n.to}
                  href={n.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={testId}
                  className="text-[14px] font-medium transition-colors duration-200 text-neutral-700 hover:mv-green-text inline-flex items-center gap-1"
                >
                  {n.label} <ExternalLink size={11} />
                </a>
              );
            }
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={testId}
                className={({ isActive }) =>
                  `text-[14px] font-medium transition-colors duration-200 ${
                    isActive ? "mv-green-text" : "text-neutral-700 hover:mv-green-text"
                  }`
                }
              >
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <LanguageSwitcher variant="header" />
          {user && user.email ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" className="btn-ghost" data-testid="header-admin-link">Admin</Link>
              )}
              <Link to="/dashboard" className="btn-ghost" data-testid="header-dashboard-link">{t("cta.personal_log")}</Link>
              <button
                onClick={async () => { await logout(); navigate("/"); }}
                className="btn-green"
                data-testid="header-logout-btn"
              >{t("cta.logout")}</button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-ghost" data-testid="header-login-link">{t("cta.login")}</Link>
              <Link to="/waiting-list" className="btn-gold" data-testid="header-open-account-btn">
                Join Waiting List
              </Link>
            </>
          )}
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <LanguageSwitcher variant="header" />
          <button
            className="p-2 rounded-full hover:bg-black/5"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-black/5 bg-white">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-3">
            {nav.map((n, i) => {
              const testId = `mobile-nav-${["pula","home","how-it-works","projects","safety","about","agent-portal","legal-status","pula-community"][i]}`;
              if (n.external) {
                return (
                  <a
                    key={n.to}
                    href={n.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setOpen(false)}
                    className="py-2 text-[15px] font-medium text-neutral-700 inline-flex items-center gap-1"
                    data-testid={testId}
                  >
                    {n.label} <ExternalLink size={12} />
                  </a>
                );
              }
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `py-2 text-[15px] font-medium ${isActive ? "mv-green-text" : "text-neutral-700"}`
                  }
                  data-testid={testId}
                >
                  {n.label}
                </NavLink>
              );
            })}
            <div className="flex gap-3 mt-2">
              {user && user.email ? (
                <>
                  <Link to="/dashboard" className="btn-ghost flex-1 justify-center" onClick={()=>setOpen(false)}>{t("cta.personal_log")}</Link>
                  <button onClick={async ()=>{ await logout(); setOpen(false); navigate("/");}} className="btn-green flex-1 justify-center">{t("cta.logout")}</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="btn-ghost flex-1 justify-center" onClick={()=>setOpen(false)}>{t("cta.login")}</Link>
                  <Link to="/waiting-list" className="btn-gold flex-1 justify-center" onClick={()=>setOpen(false)}>Join Waiting List</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
